import React from 'react';
import { Link } from 'react-router-dom';
import { Building2, ClipboardCheck, Megaphone, LineChart, ArrowRight } from 'lucide-react';
import Reveal from '@/components/site/Reveal';
import SectionHeading from '@/components/site/SectionHeading';

const SERVICES = [
  {
    icon: Building2,
    title: 'Commercial Leasing',
    description: 'Office, retail, coworking and mixed-use leasing handled end-to-end — from market positioning to lease registration.',
    items: ['Office Leasing', 'Retail Leasing', 'Coworking Leasing', 'Tenant Representation', 'Landlord Representation', 'Lease Negotiation'],
  },
  {
    icon: ClipboardCheck,
    title: 'Property Management',
    description: 'Disciplined day-to-day operations that protect asset value and tenant experience.',
    items: ['Facility Coordination', 'Vendor Management', 'Rent Collection Oversight', 'Tenant Relations', 'Asset Performance Monitoring', 'Occupancy Optimization'],
  },
  {
    icon: Megaphone,
    title: 'Marketing & Tenant Acquisition',
    description: 'Targeted marketing programs that shorten vacancy windows and attract better-quality tenants.',
    items: ['Property Branding', 'Digital Marketing', 'Lead Generation', 'Property Showings', 'Market Positioning'],
  },
  {
    icon: LineChart,
    title: 'Real Estate Advisory',
    description: 'Data-led advisory for portfolio owners, investors, and corporate occupiers.',
    items: ['Market Analysis', 'Rental Benchmarking', 'Investment Advisory', 'Portfolio Strategy'],
  },
];

export default function Services() {
  return (
    <section id="services" data-testid="home-services" className="bg-slate-50 py-20 md:py-28">
      <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
          <Reveal>
            <SectionHeading
              kicker="What we do"
              title="End-to-end commercial real estate services."
              subtitle="A single, accountable partner for leasing, management, marketing, and advisory — calibrated to the Hyderabad commercial market."
            />
          </Reveal>
          <Reveal delay={150}>
            <Link to="/services" className="hidden md:inline-flex items-center gap-2 text-sm font-semibold text-[#144726] hover:text-[#C6A35B] transition-colors link-gold" data-testid="services-view-all">
              View all services <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>

        <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2">
          {SERVICES.map((s, idx) => (
            <Reveal key={s.title} delay={idx * 80}>
              <div data-testid="services-service-card" className="group h-full rounded-xl border border-slate-200 bg-white p-6 md:p-8 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex h-12 w-12 items-center justify-center rounded-md bg-[#C6A35B]/12 text-[#144726]">
                  <s.icon className="h-6 w-6" />
                </div>
                <h3 className="mt-5 font-display text-2xl font-semibold text-[#144726] tracking-tight">{s.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-slate-600">{s.description}</p>
                <ul className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-sm text-slate-700">
                  {s.items.map((i) => (
                    <li key={i} className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-[#C6A35B]" />{i}</li>
                  ))}
                </ul>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
